import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TripStatus } from "../../../entities/trip";
import * as Haptics from "@/shared/platform/haptics";
import { useRealtimeConnected, useTripLiveUpdates, useTripRoomJoined } from "@/shared/realtime";
import {
  abandonTrip,
  adjustItem,
  advanceTripStop,
  approveItem,
  cancelTrip,
  checkApprovalTimeout,
  confirmStopApproval,
  createTrip,
  completeTripProvision,
  disputeTrip,
  estimateTrip,
  finalizeTrip,
  getTrip,
  listTrips,
  rateTrip,
  setStopMerchant,
  submitPickupProof,
  submitStopForApproval,
  transitionTrip,
  verifyTripItem,
  type EstimateTripPayload,
} from "../services/tripService";
import type {
  AbandonTripPayload,
  AdjustItemPayload,
  AdvanceStopPayload,
  ApprovalSummary,
  ApproveItemPayload,
  CreateTripPayload,
  FinalizeTripPayload,
  PickupProofPayload,
  TransitionTripPayload,
  Trip,
  StopMerchantPayload,
  TripDisputePayload,
  TripRatingPayload,
  VerifyItemPayload,
} from "../../../entities/trip";
import { WALLET_QUERY_KEY } from "../../wallet/hooks/useWalletTransactions";

export const TRIP_QUERY_KEY = ["trips"] as const;

// ─── Devis avant création ─────────────────────────────────────────────────────
// `POST /trips/estimate` : le serveur chiffre les frais ET le montant qu'il
// bloquera (`hold_amount`). Remplace la reconstitution locale — le client voit
// désormais exactement ce qui quittera son wallet.
//
// Fraîcheur maximale : le solde et les frais évoluent côté serveur sans que le
// mobile en soit notifié. Une caution périmée affichée sur un écran de
// confirmation vaudrait mieux ne pas être affichée du tout.
export function useTripEstimate(payload: EstimateTripPayload | null) {
  return useQuery({
    queryKey: [...TRIP_QUERY_KEY, "estimate", payload],
    queryFn:  () => estimateTrip(payload!),
    // Sans distance, /pricing refuse la simulation — inutile d'appeler.
    // Et sans article sur CHAQUE arrêt, le backend répond 400 VALIDATION_ERROR
    // (« stops.N.items field is required » : un tableau vide y vaut absent).
    // La garde vit ici, au point de passage commun, pour qu'aucun appelant ne
    // puisse reposer la question — un devis sans article n'a de toute façon
    // rien à chiffrer.
    enabled:
      payload != null &&
      payload.distance_km > 0 &&
      payload.stops.length > 0 &&
      payload.stops.every((s) => s.items.length > 0),
    staleTime:      0,
    refetchOnMount: "always",
    // L'écran affiche l'erreur et laisse corriger : pas d'insistance réseau.
    retry:    1,
  });
}

// ─── Lectures ─────────────────────────────────────────────────────────────────

/**
 * Détail d'une course, TENU À JOUR PAR LE TEMPS RÉEL.
 *
 * ## Le renversement du 2026-08-26 (R11)
 * Ce hook sondait `GET /trips/{id}` toutes les vingt secondes, inconditionnellement,
 * « faute de socket dédié au Trip ». La salle existe pourtant : `trip:{id}`
 * reçoit changements d'état, offres, expirations, abandons et demandes de
 * validation — et, depuis ce contrat, la position de la Main. Le canal restait
 * vide simplement parce que personne n'y entrait.
 *
 * Le sondage n'est donc plus le chemin, mais le FILET : il ne tourne que
 * lorsque la connexion est coupée. Le contrat le dit sans détour — « le temps
 * réel n'est pas une optimisation : c'est le seul chemin par lequel une
 * position de Main, une offre ou une expiration de validation atteignent
 * l'écran au moment où elles se produisent ».
 *
 * L'abonnement vit ICI, dans le hook que tout écran de course appelle déjà,
 * plutôt que dans chaque écran : impossible d'oublier d'écouter, et la salle
 * n'est demandée qu'une fois grâce au comptage d'abonnés du client socket.
 */
export function useTrip(tripId: string | undefined, enabled = true) {
  // Rejoint `trip:{id}` et invalide cette même requête à chaque événement.
  useTripLiveUpdates(enabled ? tripId : undefined);

  /**
   * ⚠️ On regarde la SALLE, pas la socket.
   *
   * Le repli était coupé dès que `useRealtimeConnected()` disait vrai. Or une
   * socket branchée ne garantit rien sur CETTE course : l'entrée en salle a pu
   * être refusée définitivement, être encore en attente avec un délai qui monte
   * à trente secondes, ou avoir été perdue par une reconnexion.
   *
   * Dans ces cas la connexion allait bien, aucun événement n'arrivait, et le
   * sondage était éteint : l'écran se figeait sans que rien ne le signale. Une
   * Main devant son comptoir ne voyait la validation du client qu'en
   * rechargeant sa page — et c'est ainsi qu'on le découvrait.
   */
  const roomJoined = useTripRoomJoined(enabled ? tripId : undefined);

  return useQuery({
    queryKey:  [...TRIP_QUERY_KEY, "detail", tripId],
    queryFn:   () => getTrip(tripId!),
    staleTime: 15_000,
    refetchInterval: (query) => {
      // ⚠️ ATTENTE DE VALIDATION : on sonde, salle acquise ou non.
      //
      // Dans cet état, la Main est BLOQUÉE devant le comptoir et n'a plus rien
      // à faire qu'attendre la décision du client. Or l'écran ne repartait que
      // sur un événement — et rien ne garantit qu'il en existe un pour la
      // RÉPONSE du client : la liste des événements de course n'en portait
      // aucun, et c'est ce qui laissait la Main sur « En attente de la
      // validation… » jusqu'à ce qu'elle recharge sa page.
      //
      // Le relais générique de `subscribeTrip` couvre désormais les noms
      // inconnus, mais il dépend encore du serveur pour ÉMETTRE quelque chose.
      // Ce sondage-ci ne dépend de personne. Dix secondes sur une fenêtre qui
      // dure une minute, contre une Main immobile : le calcul est vite fait.
      if (query.state.data?.status === "AWAITING_CLIENT_APPROVAL") return 10_000;

      // Ailleurs : tant que la salle est acquise, c'est elle qui déclenche les
      // relectures — au moment où l'événement survient, pas vingt secondes plus
      // tard.
      return roomJoined ? false : 20_000;
    },
    enabled:   enabled && Boolean(tripId),
  });
}

// NOTE : l'ancien hook `useAgentTrips` (listTrips({}) « scopé JWT ») a été retiré —
// le backend ne scope PAS GET /trips par le token pour un agent. La liste agent
// passe par useAgentActiveTrips (features/agent) qui filtre par agent_id.

export function useClientTrips(userId: string | undefined, enabled = true) {
  return useQuery({
    queryKey:  [...TRIP_QUERY_KEY, "client", userId],
    queryFn:   () => listTrips({ user_id: userId! }),
    staleTime: 30_000,
    enabled:   enabled && Boolean(userId),
  });
}

const ACTIVE_TRIP_STATUSES = new Set<TripStatus>([
  "PENDING_WALLET_CHECK",
  "WALLET_FUNDS_BLOCKED",
  "AGENT_OFFER_PENDING",
  "AGENT_ASSIGNED",
  "AGENT_EN_ROUTE",
  "IN_PROGRESS",
  "AWAITING_CLIENT_APPROVAL",
  "DELIVERING",
]);

// Courses actives du client connecté. Appelle GET /trips sans user_id :
// le backend filtre automatiquement par JWT (user_id est réservé ADMIN/SUPERVISOR).
/**
 * Courses actives du client connecté.
 *
 * ⚠️ Ce sondage-ci SUBSISTE, et volontairement : il sert à DÉCOUVRIR une course
 * dont l'app ignore encore l'identifiant — donc dont elle ne peut pas rejoindre
 * la salle. Aucun événement documenté ne couvre ce cas. R11 interdit de
 * remplacer l'écoute par du sondage ; il n'interdit pas de sonder ce qui n'est
 * pas écoutable.
 *
 * Une fois la course connue, `useTrip` prend le relais en temps réel — d'où la
 * cadence relâchée quand la socket répond : les changements d'état arrivent
 * déjà par la salle, cette liste ne sert plus qu'à repérer une NOUVELLE course.
 */
export function useMyActiveTrips() {
  const connected = useRealtimeConnected();
  return useQuery({
    queryKey:  [...TRIP_QUERY_KEY, "my", "active"],
    queryFn:   async () => {
      const trips = await listTrips({});
      return (trips ?? []).filter((t) => t.status && ACTIVE_TRIP_STATUSES.has(t.status));
    },
    staleTime:       15_000,
    refetchInterval: connected ? 60_000 : 20_000,
  });
}

// ─── Mutation partagée : pousse le Trip à jour dans le cache détail + invalide les listes
function useTripMutation<TVars>(
  mutationFn: (vars: TVars) => Promise<Trip>,
  tripId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async (trip) => {
      const id = trip.id ?? tripId;
      if (id) {
        // ⚠️ ANNULER AVANT D'ÉCRIRE. Une lecture de la course peut être EN VOL,
        // partie d'une invalidation antérieure — le complément de provision
        // invalide la course, puis on confirme dans la foulée.
        //
        // Cette lecture-là est partie AVANT la mutation : elle rapporte l'état
        // d'avant. Et `invalidateQueries` ne la remplace pas, il se raccroche à
        // elle (`cancelRefetch: false`). Elle atterrit donc APRÈS notre
        // `setQueryData` et l'écrase — une course confirmée redeviendrait « en
        // attente de validation », et le client reconfirmerait un arrêt que le
        // serveur a déjà enregistré.
        //
        // ⚠️ HONNÊTETÉ SUR L'ORIGINE. Cette garde a été écrite le 2026-09-05 en
        // croyant expliquer la boucle « complétez la provision » — à tort. Le
        // backend a tranché le lendemain : la cause était chez lui, deux calculs
        // divergents du montant requis (cf. `provisionComplement`). La course de
        // vitesse décrite ici n'a jamais été observée en production.
        //
        // On la garde quand même : elle est réelle au regard de la sémantique de
        // react-query, elle coûte un appel, et elle protège TOUTE mutation de
        // course, pas seulement ce parcours-là. Mais ce n'est pas le récit d'un
        // incident — c'est une précaution.
        await qc.cancelQueries({ queryKey: [...TRIP_QUERY_KEY, "detail", id] });
        qc.setQueryData([...TRIP_QUERY_KEY, "detail", id], trip);
      }
      qc.invalidateQueries({ queryKey: TRIP_QUERY_KEY });
      // TOUTE mutation de course touche le wallet côté serveur (hold ~120% à la
      // création, release à l'annulation, capture à la finalisation, ajustements
      // pendant l'approbation). Sans cette invalidation, la carte solde de la
      // Home et l'onglet Wallet gardaient des montants disponible/gelé périmés
      // jusqu'au prochain refetch — les mutations Order et SOS invalidaient
      // déjà le wallet, les Trips étaient l'angle mort.
      qc.invalidateQueries({ queryKey: WALLET_QUERY_KEY });
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    },
    onError: () => {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    },
  });
}

// ─── Création (CLIENT) ──────────────────────────────────────────────────────
// `idempotencyKey` stable fourni par l'écran → re-tap après timeout = même clé,
// le serveur dédoublonne le hold (pas de double caution).
export function useCreateTrip() {
  return useTripMutation<{ payload: CreateTripPayload; idempotencyKey?: string }>(
    ({ payload, idempotencyKey }) => createTrip(payload, idempotencyKey),
  );
}

// ─── Exécution (AGENT) ──────────────────────────────────────────────────────
export function useAdvanceTripStop(tripId: string) {
  return useTripMutation<{ stopIndex: number; payload: AdvanceStopPayload }>(
    ({ stopIndex, payload }) => advanceTripStop(tripId, stopIndex, payload),
    tripId,
  );
}

export function useTransitionTrip(tripId: string) {
  return useTripMutation<TransitionTripPayload>(
    (payload) => transitionTrip(tripId, payload),
    tripId,
  );
}

// ─── Règlement / annulation ─────────────────────────────────────────────────
// L'UI doit désactiver l'action pendant `isPending` : finalizeTrip n'a pas
// d'Idempotency-Key côté contrat, le garde-fou anti double-capture est ici.
export function useFinalizeTrip(tripId: string) {
  return useTripMutation<FinalizeTripPayload | undefined>(
    (payload) => finalizeTrip(tripId, payload),
    tripId,
  );
}

/**
 * Annulation par le CLIENT.
 *
 * ⚠️ N'appelez pas ce hook depuis l'espace agent : `/cancel` répond `403` à une
 * Main. C'est `useAbandonTrip` qui lui est destiné.
 *
 * L'écran doit vérifier `canClientCancel(status)` AVANT de proposer le bouton :
 * depuis le 2026-08-26, l'annulation est fermée dès `AGENT_ASSIGNED` (`409`) et
 * ouverte pendant `AGENT_OFFER_PENDING`.
 */
export function useCancelTrip(tripId: string) {
  return useTripMutation<void>(() => cancelTrip(tripId), tripId);
}

/**
 * La Main REND la course (`POST /trips/{id}/abandon`).
 *
 * Remplace l'appel à `/cancel` du bouton agent, qui répondait `403` en silence.
 * La course repart en recherche : le client garde sa commande et ses fonds
 * restent bloqués, seul le porteur change.
 */
export function useAbandonTrip(tripId: string) {
  return useTripMutation<AbandonTripPayload>(
    (payload) => abandonTrip(tripId, payload),
    tripId,
  );
}

// ─── Bouclier Qualité — vérification AGENT ───────────────────────────────────
// L'agent vérifie un article (dispo + photo + prix), puis soumet l'arrêt.
export function useVerifyTripItem(tripId: string) {
  return useTripMutation<{ stopIndex: number; itemId: string; payload: VerifyItemPayload }>(
    ({ stopIndex, itemId, payload }) => verifyTripItem(tripId, stopIndex, itemId, payload),
    tripId,
  );
}

export function useSubmitStopForApproval(tripId: string) {
  return useTripMutation<{ stopIndex: number }>(
    ({ stopIndex }) => submitStopForApproval(tripId, stopIndex),
    tripId,
  );
}

/**
 * Preuve de retrait d'une LIVRAISON.
 *
 * L'arrêt doit déjà être `AT_STOP` (arrivée signalée) et la course être une
 * LIVRAISON — sur une COURSE, la route répond `409` et c'est
 * `useSubmitStopForApproval` qu'il faut.
 */
export function useSubmitPickupProof(tripId: string) {
  return useTripMutation<{ stopIndex: number; payload: PickupProofPayload }>(
    ({ stopIndex, payload }) => submitPickupProof(tripId, stopIndex, payload),
    tripId,
  );
}

// ─── Bouclier Qualité — décision CLIENT ──────────────────────────────────────
// approve/adjust renvoient un ApprovalSummary (récap solde/deadline). On met le
// cache détail à jour depuis `summary.trip` mais on RETOURNE le récap complet au
// composant (pour afficher balance_sufficient / total / deadline).
function useApprovalSummaryMutation<TVars>(
  mutationFn: (vars: TVars) => Promise<ApprovalSummary>,
  tripId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (summary) => {
      if (summary.trip) {
        const id = summary.trip.id ?? tripId;
        qc.setQueryData([...TRIP_QUERY_KEY, "detail", id], summary.trip);
      }
      qc.invalidateQueries({ queryKey: TRIP_QUERY_KEY });
      // Solde insuffisant après ajustement : feedback distinct (warning) pour que
      // l'UI puisse alerter sans traiter ça comme un succès franc.
      Haptics.notificationAsync(
        summary.balance_sufficient === false
          ? Haptics.NotificationFeedbackType.Warning
          : Haptics.NotificationFeedbackType.Success,
      );
    },
    onError: () => {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    },
  });
}

export function useApproveItem(tripId: string) {
  return useApprovalSummaryMutation<{ stopIndex: number; itemId: string; payload: ApproveItemPayload }>(
    ({ stopIndex, itemId, payload }) => approveItem(tripId, stopIndex, itemId, payload),
    tripId,
  );
}

export function useAdjustItem(tripId: string) {
  return useApprovalSummaryMutation<{ stopIndex: number; itemId: string; payload: AdjustItemPayload }>(
    ({ stopIndex, itemId, payload }) => adjustItem(tripId, stopIndex, itemId, payload),
    tripId,
  );
}

export function useConfirmStopApproval(tripId: string) {
  return useTripMutation<{ stopIndex: number }>(
    ({ stopIndex }) => confirmStopApproval(tripId, stopIndex),
    tripId,
  );
}

// Matérialise l'expiration du délai de validation (APPROVAL_EXPIRED côté serveur
// si le client n'a pas répondu). À déclencher quand l'UI détecte la deadline
// dépassée — le serveur tranche, l'app ne fait que le notifier.
export function useCheckApprovalTimeout(tripId: string) {
  return useTripMutation<void>(() => checkApprovalTimeout(tripId), tripId);
}


// ─── Relevé de la boutique (AGENT) ──────────────────────────────────────────

/**
 * Enregistre le nom et le numéro du vendeur pour un arrêt.
 *
 * Sans ce relevé, la file de versement n'a personne à payer et la Main reste
 * devant le comptoir : depuis le 2026-09-04, c'est vers ce numéro que la
 * plateforme décaisse.
 */
export function useSetStopMerchant(tripId: string) {
  return useTripMutation<{ stopIndex: number; payload: StopMerchantPayload }>(
    ({ stopIndex, payload }) => setStopMerchant(tripId, stopIndex, payload),
    tripId,
  );
}

// ─── Note et litige (CLIENT) ────────────────────────────────────────────────

/** Note d'une course achevée. Cf. `lib/feedbackPolicy.canRateTrip`. */
export function useRateTrip(tripId: string) {
  return useTripMutation<TripRatingPayload>((payload) => rateTrip(tripId, payload), tripId);
}

/**
 * Ouvre un litige sur une course.
 *
 * ⚠️ Le `409` d'un SECOND litige n'est pas une panne : le premier suit son
 * cours. C'est à l'écran de montrer le litige existant plutôt que d'inviter à
 * réessayer (cf. `lib/feedbackPolicy.hasOpenDispute`).
 */
export function useDisputeTrip(tripId: string) {
  return useTripMutation<TripDisputePayload>((payload) => disputeTrip(tripId, payload), tripId);
}

/**
 * Complète la provision d'une course, puis laisse l'appelant reconfirmer.
 *
 * Volontairement SANS enchaînement automatique vers `confirm-approval` : les
 * deux appels touchent à l'argent, et les fondre en un seul rendrait
 * indiscernable, en cas d'échec, ce qui a été bloqué de ce qui a été confirmé.
 * L'écran enchaîne lui-même, et sait alors laquelle des deux étapes a échoué.
 */
export function useCompleteProvision(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => completeTripProvision(tripId),
    onSuccess: () => {
      // La course ET le portefeuille ont bougé : la provision vient de
      // grandir. Sans ces deux invalidations, l'écran de validation garderait
      // l'ancien plafond et la carte solde un disponible périmé — le client
      // relirait le montant qui vient précisément de changer.
      void qc.invalidateQueries({ queryKey: [...TRIP_QUERY_KEY, "detail", tripId] });
      void qc.invalidateQueries({ queryKey: WALLET_QUERY_KEY });
    },
  });
}
